/* eslint-disable jira/integration/test-id-by-folder-structure */

import React from 'react';

import noop from 'lodash/noop';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import Form from '@atlaskit/form/form';
import { FormHeader } from '@atlaskit/form/form-header';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';

import { Text } from '@atlaskit/primitives/compiled/text';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import type { ValueType } from '@atlaskit/select/types';
import { cssMap } from '@atlaskit/css';
import { defineMessage, useIntl } from '@atlassian/jira-intl';

import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { SelectOption, StageConnectProps } from './StageProps.types.tsx';
import type { ApplicationLinkRemoteOAuth2ApplicationLink } from './CommonApplicationLinkCreationEntity.tsx';

import { putOAuth2ConfigInfo } from './fetchApplinkManifestForUrl.tsx';
import CredentialsForm from './CredentialsForm.tsx';
import { SubpageTemplate } from './SubpageTemplate.tsx';
import { formatBackendError } from './formatBackendError.tsx';

const styles = cssMap({
	fullContainer: {
		height: '100%',
	},
});

const connectHeaderMessage = defineMessage({
	defaultMessage: 'Connect to the application',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-header',
	description: 'Header for the connect step in the application link creation flow.',
});

const connectHeaderCloudConnectorMessage = defineMessage({
	defaultMessage: 'Choose scopes',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-header-cloud-connector',
	description:
		'Header for the choose-scope step in the cloud connector application link creation flow.',
});

const connectDescriptionMessage = defineMessage({
	defaultMessage:
		'Copy and paste the details below between this page and the settings page of the external application where you configure application links. This creates a secure connection.',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-description',
	description: 'Description explaining how to use the connect applications form.',
});

const connectDescriptionCloudConnectorMessage = defineMessage({
	defaultMessage:
		'Select the permission scopes that control how your app can interact with the remote DC app.',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-description-cloud-connector',
	description:
		'Description explaining the choose-scope step in the cloud connector application link creation flow.',
});

const connectContinueButtonMessage = defineMessage({
	defaultMessage: 'Connect',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-continue-button',
	description: 'Label for the button to submit/test the connection process.',
});

const connectContinueButtonCloudConnectorMessage = defineMessage({
	defaultMessage: 'Save',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-continue-button-cloud-connector',
	description:
		'Label for the button to save the chosen scopes in the cloud connector application link creation flow.',
});

const connectDoLaterButtonMessage = defineMessage({
	defaultMessage: 'Do this later',
	id: 'applinks.administration.ui.creation-flow-dialog.connect-do-later-button',
	description: 'Label for the button to postpone the connection process.',
});

const requiredFieldsTextMessage = defineMessage({
	defaultMessage: 'Required fields are marked with an asterisk',
	id: 'applinks.administration.ui.creation-flow-dialog.required-fields-text',
	description: 'Text indicating that required fields in a form are marked with an asterisk.',
});

const unknownErrorTextMessage = defineMessage({
	defaultMessage: 'We encountered an unknown error.',
	id: 'applinks.administration.ui.unknown-error',
	description:
		'A generic error message for when something goes wrong, but without any further detail.',
});

type ConnectSubpageProps = {
	state: StageConnectProps;
	onSuccess: () => void;
	onError: (error: string) => void;
	onCancel: () => void;
};

const ConnectSubpage = (props: ConnectSubpageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const isCloudConnector = !!props.state.applink.properties?.CLOUD_CONNECTOR_PRODUCT_CONNECTION_ID;

	const onFormSubmit = async (data: {
		remoteApplicationSupportedScopes?: ValueType<SelectOption, true>;
		applicationClientId: string;
		applicationClientSecret: string;
		redirectUrl?: string;
	}) => {
		const scopes = data.remoteApplicationSupportedScopes
			? data.remoteApplicationSupportedScopes.map((scope) => scope.value)
			: [];

		const oauth2Config: ApplicationLinkRemoteOAuth2ApplicationLink = {
			remoteClientId: data.applicationClientId,
			remoteClientSecret: data.applicationClientSecret,
			remoteRedirectUrl: data.redirectUrl,

			outboundScopes: scopes,
		};

		try {
			await putOAuth2ConfigInfo(props.state.applink.id, oauth2Config);
			props.onSuccess(); // Success = close the drawer
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			log.safeErrorWithoutCustomerData(
				'applinks.administration.list.creation-flow-dialog.connect.submitConnectForm',
				'Failed to submit credentials form for newly created OAuth 2.0 applink',
				maybeError,
			);
			if (fg('applinks-1093-redirect-error-fix')) {
				props.onError(formatBackendError(maybeError, formatMessage(unknownErrorTextMessage)));
			} else {
				props.onError(maybeError ? maybeError.message : formatMessage(unknownErrorTextMessage));
			}
			return err;
		}
	};

	return (
		<Form<{
			remoteApplicationSupportedScopes?: ValueType<SelectOption, true>;
			applicationClientId: string;
			applicationClientSecret: string;
			redirectUrl?: string;
			displayName: string;
		}>
			onSubmit={onFormSubmit}
			xcss={styles.fullContainer}
		>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<SubpageTemplate
						variant={props.state.variant}
						kind={props.state.kind}
						buttons={
							<ButtonGroup>
								<Button appearance="subtle" onClick={props.onCancel}>
									{formatMessage(connectDoLaterButtonMessage)}
								</Button>
								<Button
									testId="applinks-administration.ui.list.creation-flow-dialog.connect.submit-button"
									type="submit"
									appearance="primary"
									isLoading={submitting}
								>
									{formatMessage(
										isCloudConnector
											? connectContinueButtonCloudConnectorMessage
											: connectContinueButtonMessage,
									)}
								</Button>
							</ButtonGroup>
						}
						content={
							<Stack space="space.200">
								<FormHeader
									title={formatMessage(
										isCloudConnector ? connectHeaderCloudConnectorMessage : connectHeaderMessage,
									)}
								>
									<Stack space="space.200">
										<Text>
											{formatMessage(
												isCloudConnector
													? connectDescriptionCloudConnectorMessage
													: connectDescriptionMessage,
											)}
										</Text>
									</Stack>

									<Text aria-hidden="true" size="small">
										{formatMessage(requiredFieldsTextMessage)} <RequiredAsterisk />
									</Text>
								</FormHeader>

								<CredentialsForm
									applink={props.state.applink}
									oauth2ConfigInfo={props.state.oauth2ConfigInfo}
									onDisplayError={props.onError}
									onRequestStatusRefresh={noop}
									partiallyLinkedMode
								/>
							</Stack>
						}
					/>
				</form>
			)}
		</Form>
	);
};

export default ConnectSubpage;
